import Image from 'next/image';

import React, { useEffect, useState } from 'react';

import LogoIcon from 'public/img/miso-logo-icon.png';
import styled from 'styled-components';

import { Container } from '@components/parts/Container';

import { MISO_APP_STORES } from '@utils/constants';
import { getVisibility } from '@utils/get-visibility';

import { colors } from '@styles/colors';

import { GlobalData } from '@customTypes/GlobalData';
import { MenuItem } from '@customTypes/MenuItem';

import { ChevronDown, ChevronUp } from './FormIcon';

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: white;
  min-height: 60px;
  @media (min-width: 992px) {
    height: 80px;
  }
  .container {
    height: 100%;
    display: flex;
    align-items: center;
    .header-content {
      position: relative;
      height: 100%;
      @media (min-width: 992px) {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
      }
      flex-direction: column;
      div.logo-container {
        display: flex;
        align-items: center;
        height: 60px;
        margin-left: 18px;
        @media (min-width: 992px) {
          margin-left: 0;
        }
      }
      button.hamburger {
        cursor: pointer;
        @media (min-width: 992px) {
          display: none;
        }
        position: absolute;
        right: 0;
        top: 0;
        width: 60px;
        height: 60px;
        background: no-repeat center url('/svg/hamburger.svg');
        background-size: 40%;
        border: none;
        outline: none;
        &.open {
          background: no-repeat center url('/svg/close.svg');
        }
      }
      &.open {
        ul.menus {
          display: flex;
        }
      }
      flex: 1;
      ul.menus {
        height: 100%;
        display: none;
        margin-left: -24px;
        margin-right: -24px;
        @media (min-width: 992px) {
          display: flex;
          flex-direction: row;
        }
        flex-direction: column;
        flex-wrap: wrap;
        list-style-type: none;
        margin-block: 0;
        margin-inline: 0;
        padding-inline: 0;
        @media (max-width: 991px) {
          height: 100vh;
        }
        li {
          font-size: 18px;
          margin-bottom: 0;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          height: 100%;
          color: #2e3035;
          font-weight: 500;
          justify-content: center;
          border-top: 4px solid white;
          border-bottom: 4px solid white;
          &.main-menu {
            padding: 0;
            @media (max-width: 991px) {
              flex-direction: column;
              height: auto;
            }
            div.group {
              display: flex;
              align-items: center;
              min-height: 52px;
              .clickable {
                width: 100%;
                height: 100%;
                a {
                  display: flex;
                  align-items: center;
                  width: 100%;
                  height: 100%;
                }
                display: flex;
                align-items: center;
                padding: 0 16px;
                @media (min-width: 991px) {
                  svg {
                    display: none;
                  }
                }

                @media (min-width: 1200px) {
                  padding: 0 20px;
                }
              }
              @media (max-width: 991px) {
                font-size: 18px;
                font-weight: 500;
              }
            }
            &:nth-child(n + 4) {
              div.group {
                font-size: inherit;
                font-weight: 500;
              }
            }
          }
          svg.chevron {
            margin-left: 0.5em;
            width: 10px;
          }
          position: relative;
          ul.submenus {
            display: none;
            padding-inline-start: 0;
            @media (min-width: 992px) {
              position: absolute;
              top: calc(100% + 4px);
              left: 0;
              min-width: 100%;
              white-space: nowrap;
            }
            li {
              border: none;
              height: auto;
              background: white;
              padding: 0;
              @media (max-width: 991px) {
                background: #f2f2f2;
                width: 100%;
              }
              a {
                display: flex;
                gap: 10px;
                align-items: center;
                width: 100%;
                height: 100%;
                padding: 12px;
                @media (min-width: 992px) {
                  padding: 12px 40px 12px 24px;
                }
                @media (max-width: 991px) {
                  color: #62656a;
                }
                font-size: 16px;

                img {
                  @media (max-width: 375px) {
                    width: 28px;
                    height: 28px;
                  }
                }
              }
              &:hover {
                background: #f2f2f2;
              }
            }
          }
          &:hover {
            color: #055cf5;
            svg.chevron {
              path {
                fill: #055cf5;
              }
            }
            &.main-menu {
              @media (min-width: 992px) {
                &:hover {
                  background: inherit;
                }
              }
            }
            ul.submenus {
              display: block;

              @media (max-width: 991px) {
                margin: 0;
                padding: 0;
                background: #f2f2f2;
              }

              @media (min-width: 992px) {
                margin: 0;
                background: white;
                border-radius: 2px;
                border: 1px solid #e3e5ea;
              }
            }
          }
        }
      }
    }
  }
`;

const FixedMenuWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 2rem;
  @media (max-width: 991px) {
    flex-direction: column;
    margin-left: 0;
    align-items: flex-start;
    padding: 10px 0;
    margin-top: 10px;
    border-top: 1px solid #e3e5ea;
  }

  @media (max-width: 375px) {
    padding: 8px 0;
    margin-top: 8px;
  }
`;

const FixedMenu = styled.a`
  padding: 12px 16px;
  font-size: 16px;
  color: #2e3035;
  font-weight: 400;

  @media (max-width: 375px) {
    font-size: 14px;
    padding: 10px 16px;
  }
`;

const ClosingArea = styled.div<{ height?: string; $flexGrow?: string }>`
  display: none;
  @media (max-width: 991px) {
    display: block;
    width: 100%;
    height: ${({ height }) => (height ? height : '0')};
    flex-grow: ${({ $flexGrow }) => ($flexGrow ? $flexGrow : '0')};
  }
`;

const GroupWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 991px) {
    padding-right: 18px;
  }

  @media (max-width: 375px) {
    padding-right: 12px;
  }
`;

const Icon = styled.div`
  @media (min-width: 992px) {
    display: none;
  }
  display: flex;
  align-items: center;
`;

const AppDownloadBanner = styled.div`
  display: none;
  @media (max-width: 991px) {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 10px 16px;
    margin-top: 10px;
  }
`;

const Content = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px;
  border-radius: 10px;
  background-color: ${colors.grey06};
`;

const Description = styled.div`
  font-size: 13px;
  line-height: 1.3;

  @media (min-width: 576px) {
    font-size: 14px;
  }

  p:first-child {
    font-weight: bold;
    margin-bottom: 2px;
  }
`;

type Props = {
  data: GlobalData;
  pageViewer: string;
};

const Header: React.FC<Props> = (props) => {
  const [open, setOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [windowWidth, setWindowWidth] = useState<number | null>(null);
  const [appStoreLink, setAppStoreLink] = useState<string>('');

  useEffect(() => {
    setWindowWidth(window.innerWidth);

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOverflowControl = (isOpen: boolean) => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  };

  const resetOverflow = () => {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  };

  useEffect(() => {
    handleOverflowControl(open);

    return () => {
      resetOverflow();
    };
  }, [open]);

  const redirectToStore = () => {
    if (typeof window === 'undefined') setAppStoreLink('');
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /android/i.test(userAgent);

    if (isIOS) {
      setAppStoreLink(MISO_APP_STORES.IOS);
    }
    if (isAndroid) {
      setAppStoreLink(MISO_APP_STORES.ANDROID);
    }
  };

  useEffect(() => {
    redirectToStore();
  }, []);

  const isMobile = windowWidth != null && windowWidth <= 991;

  const toggleMenu = (menuName: string) => {
    if (activeMenu === menuName) {
      setActiveMenu(null);
    } else {
      setActiveMenu(menuName);
    }
  };

  const { data, pageViewer } = props;
  const { headerMenus } = data;
  const isVisible = getVisibility(data.viewer_types, pageViewer);
  if (!isVisible) {
    return null;
  }
  const menus = headerMenus.reduce<{ [key: string]: MenuItem[] }>((acc, menu) => {
    if (!menu.group) return { ...acc, [menu.name]: [menu] };
    return { ...acc, [menu.group]: [...(acc[menu.group] ?? []), menu] };
  }, {});

  return (
    <Wrapper id="header">
      <Container fullOnMobile style={{ height: '100%' }}>
        <div className={`header-content ${open ? 'open' : ''}`}>
          <div className="logo-container">
            <a href="https://miso.kr">
              <Image className="logo" priority src="/img/miso-logo-blue.png" width={70} height={30} alt="Miso" />
            </a>
          </div>
          <button className={`hamburger ${open ? 'open' : ''}`} onClick={() => setOpen(!open)} />
          <ul className="menus">
            {Object.keys(menus).map((menu, idx) => (
              <li className="main-menu" key={idx}>
                <div className="group">
                  {menus[menu].length > 1 ? (
                    <GroupWrapper>
                      <div className="clickable" onClick={() => toggleMenu(menu)}>
                        {menu}
                      </div>
                      <Icon>{activeMenu === menu ? <ChevronUp /> : <ChevronDown />}</Icon>
                    </GroupWrapper>
                  ) : (
                    <div className="clickable">
                      <a href={menus[menu][0].href} onClick={() => setOpen(false)}>
                        {menu}
                      </a>
                    </div>
                  )}
                </div>
                {menus[menu].length > 1 && (isMobile ? activeMenu === menu : true) && (
                  <>
                    <ul className="submenus">
                      {menus[menu].map((submenu, sidx) => (
                        <li key={sidx}>
                          <a href={submenu.href} onClick={() => setOpen(false)}>
                            {submenu.image && (
                              <Image
                                src={submenu.image.data?.attributes.url}
                                alt={submenu.image.data?.attributes.name}
                                width={36}
                                height={36}
                              />
                            )}
                            {submenu.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </li>
            ))}
            <FixedMenuWrapper>
              <FixedMenu href={'/partner'} onClick={() => setOpen(false)}>
                파트너 지원
              </FixedMenu>
              <FixedMenu href={'https://miso.kr/support'} onClick={() => setOpen(false)}>
                고객 지원
              </FixedMenu>
            </FixedMenuWrapper>
            <ClosingArea height={'50px'} onClick={() => setOpen(false)} />
            <AppDownloadBanner>
              <Content href={appStoreLink} target="_blank" rel="noopener noreferrer">
                <Image src={LogoIcon} alt="info" width={32} height={32} />
                <Description>
                  <p>홈서비스 더 간편하게 받아보세요!</p>
                  <p>미소 앱 다운받기</p>
                </Description>
              </Content>
            </AppDownloadBanner>
            <ClosingArea $flexGrow={'1'} onClick={() => setOpen(false)} />
          </ul>
        </div>
      </Container>
    </Wrapper>
  );
};

export default Header;
